/**
 * Click stitch review — issue #963 track 3 + #972 (UX parity) +
 * #983 (two-column row layout).
 *
 * Companion to /stitch-review (URL stitches at the FE-call →
 * BE-route boundary). This page lists user-interaction handlers
 * that don't have a confident click stitch and lets the user
 * accept an AI proposal, manually bind a candidate api-call,
 * or mark the handler as UI-only.
 *
 * UX matches the URL stitch review (#812 / #818 / #827 / #830 /
 * #983) — SummaryCard filter chips + sortable list + provenance
 * badges + sticky header + 1fr|auto|1fr "handler ↔ api-call"
 * pairing grid — so users see the same affordances and visual
 * idiom at both stitch layers.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { SummaryCard, type SummaryCardTone } from '../components/stitching/SummaryCard';
import { apiFetch } from '../services/api';

interface HandlerKey {
  handlerFile: string;
  handlerFunction: string;
  triggerElement: string;
  eventType: string;
  component?: string;
}

interface UnstitchedHandler {
  handler: HandlerKey;
  component?: string;
  triggerElement: string;
  eventType: string;
  handlerFile: string;
  handlerLine?: number;
  handlerFunction: string;
  calledFunctions: string[];
}

interface ApiCallKey {
  sourceFile: string;
  method: string;
  endpoint: string;
  sourceContext?: string;
}

interface ClickStitch {
  kind: 'click';
  handler: HandlerKey;
  apiCall: ApiCallKey;
  confidence: number;
  source: 'auto' | 'user' | 'ai';
  rationale: string;
  createdAt: string;
}

interface AutoIgnored {
  handler: HandlerKey;
  reason: string;
}

interface ReviewState {
  unstitched: UnstitchedHandler[];
  totalUnstitched: number;
  unstitchedOffset: number;
  unstitchedLimit: number;
  autoStitches: ClickStitch[];
  userStitches: ClickStitch[];
  aiStitches: ClickStitch[];
  ignoredHandlers: HandlerKey[];
  autoIgnoredHandlers: AutoIgnored[];
  generatedFrom: {
    analysisCompletedAt: string;
    stitchSetGeneratedAt: string | null;
  } | null;
  // #982: timestamp of the most recent click-stitch mutation, or
  // null if flows are fresh. The review page renders a "Regenerate
  // flows" affordance when this is set.
  e2eFlowsStaleSince?: string | null;
  state: 'ready' | 'no-analysis';
}

/**
 * Auto stitches at or above this confidence are applied to flows
 * automatically (matches the threshold in
 * `e2eFlowGenerationService.ts`). Below it, the auto-pass emits a
 * candidate awaiting confirmation.
 */
const CONFIDENCE_AUTO_TRUSTED = 0.9;

type Filter = 'all' | 'in-effect' | 'candidates' | 'user' | 'ai' | 'unstitched' | 'ignored';
type Sort = 'newest' | 'oldest' | 'confidence' | 'name' | 'source';

export function ClickStitchReviewPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [data, setData] = useState<ReviewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [proposing, setProposing] = useState(false);
  const [proposeMessage, setProposeMessage] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenMessage, setRegenMessage] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('newest');

  const refresh = async () => {
    if (!projectId) return;
    try {
      // #980: load the full unstitched set in one shot — the
      // virtualised UI renders 10k+ rows without DOM blow-up, and
      // grouping/search/bulk-actions all need the complete set
      // client-side. Server caps at 50,000.
      const r = await apiFetch(`/projects/${projectId}/stitches/click/review?all=true`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown error');
    }
  };

  useEffect(() => { refresh(); }, [projectId]);

  const confidentAuto = useMemo(
    () => (data?.autoStitches ?? []).filter(s => s.confidence >= CONFIDENCE_AUTO_TRUSTED),
    [data],
  );
  const candidateAuto = useMemo(
    () => (data?.autoStitches ?? []).filter(s => s.confidence < CONFIDENCE_AUTO_TRUSTED),
    [data],
  );

  const accept = async (handler: HandlerKey, apiCall: ApiCallKey) => {
    if (!projectId) return;
    const r = await apiFetch(`/projects/${projectId}/stitches/click/bind`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handler, apiCall, note: 'accepted in review UI' }),
    });
    if (!r.ok) { setError(`Bind failed: HTTP ${r.status}`); return; }
    refresh();
  };

  const ignore = async (handler: HandlerKey) => {
    if (!projectId) return;
    const r = await apiFetch(`/projects/${projectId}/stitches/click/ignore`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handler }),
    });
    if (!r.ok) { setError(`Ignore failed: HTTP ${r.status}`); return; }
    refresh();
  };

  const unignore = async (handler: HandlerKey) => {
    if (!projectId) return;
    const r = await apiFetch(`/projects/${projectId}/stitches/click/unignore`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handler }),
    });
    if (!r.ok) { setError(`Unignore failed: HTTP ${r.status}`); return; }
    refresh();
  };

  /**
   * #982: kick off a full E2E flow regeneration so the flows page
   * reflects everything the user bound/ignored/proposed during this
   * review. Server clears `e2eFlowsStaleSince` on success.
   */
  const regenerateFlows = async () => {
    if (!projectId) return;
    setRegenerating(true);
    setRegenMessage(null);
    try {
      const r = await apiFetch(`/projects/${projectId}/e2e-flows/generate`, {
        method: 'POST',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const result = (await r.json()) as { flowCount?: number };
      setRegenMessage(
        result.flowCount != null
          ? `Regenerated ${result.flowCount} flows.`
          : 'Flows regenerated.',
      );
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown error');
    } finally {
      setRegenerating(false);
    }
  };

  const proposeAi = async () => {
    if (!projectId) return;
    setProposing(true);
    setProposeMessage(null);
    try {
      const r = await apiFetch(`/projects/${projectId}/stitches/click/propose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maxProposals: 50 }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const result = (await r.json()) as { proposed: number; skipped: number; capped: number };
      setProposeMessage(
        `AI proposed ${result.proposed}, skipped ${result.skipped}, capped ${result.capped}.`,
      );
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown error');
    } finally {
      setProposing(false);
    }
  };

  const toggleFilter = (f: Filter) => setFilter(prev => (prev === f ? 'all' : f));

  if (!projectId) return <div className="p-4">Missing project id.</div>;
  if (error) return (
    <div className="p-4 text-red-600">
      Error loading review: {error}
      <button className="ml-3 underline" onClick={() => refresh()}>Retry</button>
    </div>
  );
  if (!data) return <div className="p-4">Loading…</div>;
  if (data.state === 'no-analysis') {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-semibold mb-2">Click Stitch Review</h1>
        <p>No analysis results yet. Run analysis first.</p>
        <Link to={`/projects/${projectId}`} className="text-blue-600 underline">
          Back to project
        </Link>
      </div>
    );
  }

  const sortedConfidentAuto = sortStitches(confidentAuto, sort);
  const sortedCandidateAuto = sortStitches(candidateAuto, sort);
  const sortedUser = sortStitches(data.userStitches, sort);
  const sortedAi = sortStitches(data.aiStitches, sort);

  const showInEffect = filter === 'all' || filter === 'in-effect';
  const showCandidates = filter === 'all' || filter === 'candidates';
  const showUser = filter === 'all' || filter === 'user';
  const showAi = filter === 'all' || filter === 'ai';
  const showUnstitched = filter === 'all' || filter === 'unstitched';
  const showIgnored = filter === 'all' || filter === 'ignored';

  return (
    <div className="p-4 space-y-4">
      {/* ─── Sticky header ─── */}
      <div className="sticky top-0 z-10 bg-background pb-3 border-b border-border space-y-3">
        <div className="flex items-center justify-between pt-2">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800 dark:text-slate-200">
              Click Stitch Review
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              UI handlers → api-calls. Companion to{' '}
              <Link to={`/projects/${projectId}/stitch-review`} className="text-blue-600 hover:underline">
                URL stitch review
              </Link>
              .
            </p>
            {/* #977: provenance hint so the user knows whether they
                need to re-analyze. */}
            {data.generatedFrom?.analysisCompletedAt && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="generated-from">
                Last analyzed: {formatRelative(data.generatedFrom.analysisCompletedAt)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* #982: surface a "regen flows" button when stale, otherwise
                a softer "done — refresh flows" affordance. */}
            {data.e2eFlowsStaleSince ? (
              <button
                onClick={regenerateFlows}
                disabled={regenerating}
                data-testid="regen-flows-stale"
                title="Stitches have changed. Click to regenerate E2E flows to reflect the new pairings."
                className="px-3 py-1.5 text-sm font-medium rounded bg-amber-600 text-white disabled:opacity-50"
              >
                {regenerating ? 'Regenerating…' : 'Done — regenerate flows'}
              </button>
            ) : null}
            <button
              onClick={proposeAi}
              disabled={proposing}
              className="px-3 py-1.5 text-sm font-medium rounded bg-blue-600 text-white disabled:opacity-50"
            >
              {proposing ? 'Proposing…' : 'Propose with AI'}
            </button>
          </div>
        </div>
        {proposeMessage && (
          <div className="text-sm text-green-700">{proposeMessage}</div>
        )}
        {regenMessage && (
          <div className="text-sm text-amber-700">{regenMessage}</div>
        )}

        {/* Filter chips — shared SummaryCard with StitchReviewPage so
            the two pages can't drift visually (#974 review). */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <SummaryCard
            label="Auto, in effect"
            value={confidentAuto.length}
            tone="green"
            active={filter === 'in-effect'}
            onClick={() => toggleFilter('in-effect')}
            hint="confidence ≥ 90%"
            tooltip={
              <>
                <strong>Click stitches the auto-pass applied to flows.</strong>
                <br />
                Produced by <code>runAutoClickStitchPass</code> at or above the
                {' '}<code>CONFIDENCE_AUTO_TRUSTED = 0.9</code> threshold. These
                handler→api-call bindings show up in E2E flows automatically;
                no review needed.
              </>
            }
          />
          <SummaryCard
            label="Candidates"
            value={candidateAuto.length}
            tone="amber"
            active={filter === 'candidates'}
            onClick={() => toggleFilter('candidates')}
            hint="auto, < 90%"
            tooltip={
              <>
                <strong>Auto-pass results below the 0.9 threshold.</strong>
                <br />
                Same source as "Auto, in effect" but lower confidence — not
                applied to flows yet. Click <em>Accept</em> on a row to confirm
                the binding; it then moves into the user-confirmed bucket.
              </>
            }
          />
          <SummaryCard
            label="User-confirmed"
            value={data.userStitches.length}
            tone="blue"
            active={filter === 'user'}
            onClick={() => toggleFilter('user')}
            tooltip={
              <>
                <strong>Stitches you explicitly bound.</strong>
                <br />
                Either accepted from a candidate / AI proposal, or bound
                manually. Confidence = 1.0, source = <code>user</code>.
                Persisted in <code>stitches.json</code> under{' '}
                <code>stitches.click.user</code>.
              </>
            }
          />
          <SummaryCard
            label="AI proposals"
            value={data.aiStitches.length}
            tone="purple"
            active={filter === 'ai'}
            onClick={() => toggleFilter('ai')}
            tooltip={
              <>
                <strong>Stitches suggested by the AI proposer.</strong>
                <br />
                Source = <code>ai</code>, awaiting accept/reject. Click{' '}
                <em>Propose with AI</em> in the header to run the proposer
                across unresolved handlers.
              </>
            }
          />
          <SummaryCard
            label="Unstitched"
            value={data.totalUnstitched}
            tone={data.totalUnstitched > 0 ? 'red' : 'slate'}
            active={filter === 'unstitched'}
            onClick={() => toggleFilter('unstitched')}
            hint={
              data.unstitched.length < data.totalUnstitched
                ? `showing ${data.unstitched.length} of ${data.totalUnstitched}`
                : undefined
            }
            tooltip={
              <>
                <strong>Click handlers with no api-call attribution found.</strong>
                <br />
                The auto-pass, user binds, and AI proposals together haven't
                covered these. Auto-ignored handlers (heuristic from #979) are
                excluded from this count. Bind manually, mark UI-only, or run
                the AI proposer to reduce.
              </>
            }
          />
          <SummaryCard
            label="Ignored"
            value={data.ignoredHandlers.length + data.autoIgnoredHandlers.length}
            tone="slate"
            active={filter === 'ignored'}
            onClick={() => toggleFilter('ignored')}
            hint={
              data.autoIgnoredHandlers.length > 0
                ? `${data.autoIgnoredHandlers.length} auto · ${data.ignoredHandlers.length} user`
                : undefined
            }
            tooltipAlign="right"
            tooltip={
              <>
                <strong>Handlers marked UI-only — won't appear in flows.</strong>
                <br />
                <em>User-marked</em>: explicit "Mark as UI-only" clicks,
                persistent. <em>Auto-ignored</em>: conservative heuristic
                (state setters, <code>e.stopPropagation</code>, dismiss-pattern
                prop callbacks) — recomputed each request, not persisted.
              </>
            }
          />
        </div>

        {/* Sort toggle */}
        <SortToggle sort={sort} onChange={setSort} />
      </div>

      {/* ─── Sections (rendered conditionally on filter) ─── */}
      {showInEffect && (
        <Section title={`Auto stitches in effect (${sortedConfidentAuto.length})`} subtitle="Already applied to flows.">
          <StitchList stitches={sortedConfidentAuto} variant="in-effect" />
        </Section>
      )}
      {showCandidates && (
        <Section
          title={`Low-confidence candidates (${sortedCandidateAuto.length})`}
          subtitle="Not applied yet. Accept to apply, or skip."
        >
          <StitchList stitches={sortedCandidateAuto} variant="candidate" onAccept={accept} />
        </Section>
      )}
      {showUser && (
        <Section title={`User-confirmed stitches (${sortedUser.length})`}>
          <StitchList stitches={sortedUser} variant="user" />
        </Section>
      )}
      {showAi && (
        <Section title={`AI proposals (${sortedAi.length})`}>
          <StitchList stitches={sortedAi} variant="ai" onAccept={accept} />
        </Section>
      )}
      {showUnstitched && (
        <UnstitchedSection
          unstitched={data.unstitched}
          totalUnstitched={data.totalUnstitched}
          projectId={projectId}
          onIgnore={ignore}
          onMutated={refresh}
          onError={setError}
        />
      )}

      {/* #979: auto-ignored UI-only handlers. Heuristic-derived,
          not persisted — refreshes each request. Default-collapsed. */}
      {showIgnored && data.autoIgnoredHandlers.length > 0 && (
      <DetailsCollapsed
        title={`Auto-ignored as UI-only (${data.autoIgnoredHandlers.length})`}
        subtitle="Heuristic — handlers that only call e.stopPropagation, state setters, or dismiss-pattern callbacks. Override with Unignore if wrong."
        defaultOpen={filter === 'ignored'}
      >
        <ul className="space-y-2">
          {data.autoIgnoredHandlers.map((entry, i) => (
            <ClickStitchRow
              key={i}
              testid="auto-ignored-handler"
              variant="auto-ignored"
              handler={{ component: entry.handler.component, handlerFunction: entry.handler.handlerFunction }}
              handlerFile={entry.handler.handlerFile}
              triggerElement={entry.handler.triggerElement}
              eventType={entry.handler.eventType}
              reason={entry.reason}
            />
            // Per #984: Unignore affordance for auto-ignored is future
            // work (needs persistence layer). Intentionally no action.
          ))}
        </ul>
      </DetailsCollapsed>
      )}

      {/* Ignored handlers — visible under the 'all' filter (default
          collapsed) or when the user clicks the Ignored chip
          (auto-expands via filter check below). */}
      {showIgnored && (
      <DetailsCollapsed
        title={`User-marked UI-only (${data.ignoredHandlers.length})`}
        subtitle="Explicitly marked as UI-only. They no longer surface in the unstitched list."
        defaultOpen={filter === 'ignored'}
      >
        {data.ignoredHandlers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No handlers ignored yet.</p>
        ) : (
          <ul className="space-y-2">
            {data.ignoredHandlers.map((h, i) => (
              <ClickStitchRow
                key={i}
                testid="ignored-handler"
                variant="ignored"
                handler={{ component: h.component, handlerFunction: h.handlerFunction }}
                handlerFile={h.handlerFile}
                triggerElement={h.triggerElement}
                eventType={h.eventType}
                reason="User-marked UI-only."
                action={
                  <button
                    onClick={() => unignore(h)}
                    data-testid="unignore-handler"
                    className="px-2 py-1 text-xs rounded border border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors whitespace-nowrap"
                  >
                    Unignore
                  </button>
                }
              />
            ))}
          </ul>
        )}
      </DetailsCollapsed>
      )}
    </div>
  );
}

/**
 * #977: short relative-time helper for the analyzed-at hint. Plain
 * date math rather than a runtime dep.
 */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const seconds = Math.max(0, (now - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function sortStitches(list: ClickStitch[], sort: Sort): ClickStitch[] {
  const out = [...list];
  switch (sort) {
    case 'newest':
      out.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      break;
    case 'oldest':
      out.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      break;
    case 'confidence':
      out.sort((a, b) => b.confidence - a.confidence);
      break;
    case 'name':
      out.sort((a, b) => a.handler.handlerFunction.localeCompare(b.handler.handlerFunction));
      break;
    case 'source': {
      // user > ai > auto — same precedence as the URL sort.
      const rank = { user: 0, ai: 1, auto: 2 } as const;
      out.sort((a, b) => rank[a.source] - rank[b.source]);
      break;
    }
  }
  return out;
}

function SortToggle({ sort, onChange }: { sort: Sort; onChange: (s: Sort) => void }) {
  // Match the URL stitch review's sort options (#830) so users see
  // the same vocabulary at both layers.
  const opts: Array<{ key: Sort; label: string }> = [
    { key: 'newest', label: 'Newest' },
    { key: 'oldest', label: 'Oldest' },
    { key: 'name', label: 'A-Z' },
    { key: 'confidence', label: 'Confidence' },
    { key: 'source', label: 'Source' },
  ];
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground">Sort:</span>
      {opts.map(o => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`px-2 py-1 rounded border transition-colors ${
            sort === o.key
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
              : 'border-border text-muted-foreground hover:bg-muted'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ProvenanceBadge({ source }: { source: 'auto' | 'user' | 'ai' }) {
  // Same colour vocabulary as the SummaryCard tones above —
  // auto/user/ai → green/blue/purple — so the badge tinting on a
  // stitch row matches the filter-chip tone for that bucket.
  const tones: Record<typeof source, SummaryCardTone> = {
    auto: 'green',
    user: 'blue',
    ai: 'purple',
  };
  const tint: Record<SummaryCardTone, string> = {
    green: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    amber: '',
    red: '',
    slate: '',
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded border ${tint[tones[source]]}`}>
      {source}
    </span>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <details open className="border border-border rounded">
      <summary className="px-3 py-2 bg-muted/30 text-sm font-semibold cursor-pointer">
        {title}
        {subtitle && (
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {subtitle}
          </span>
        )}
      </summary>
      <div className="p-3">{children}</div>
    </details>
  );
}

function DetailsCollapsed({
  title,
  subtitle,
  children,
  defaultOpen,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details {...(defaultOpen ? { open: true } : {})} className="border border-border rounded">
      <summary className="px-3 py-2 bg-muted/30 text-sm font-semibold cursor-pointer">
        {title}
        {subtitle && (
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {subtitle}
          </span>
        )}
      </summary>
      <div className="p-3">{children}</div>
    </details>
  );
}

/**
 * #983: shared two-column row mirroring StitchReviewPage's
 * `1fr | auto | 1fr` grid. Left = handler ("what fired"), right =
 * api-call ("what it called"), arrow in the middle gutter so the
 * eye reads the pair at a glance.
 *
 * Variants control the right-column label/tint and the dim/strike
 * style applied to ignored rows. Actions are anchored to the right
 * edge of the provenance bar (below the grid) — same idiom as the
 * URL page.
 */
type RowVariant = 'in-effect' | 'candidate' | 'user' | 'ai' | 'unstitched' | 'ignored' | 'auto-ignored';

function ClickStitchRow({
  variant,
  handler,
  handlerFile,
  handlerLine,
  triggerElement,
  eventType,
  calledFunctions,
  apiCall,
  confidence,
  rationale,
  reason,
  source,
  action,
  testid,
}: {
  variant: RowVariant;
  handler: { component?: string; handlerFunction: string };
  handlerFile: string;
  handlerLine?: number;
  triggerElement?: string;
  eventType?: string;
  calledFunctions?: string[];
  apiCall?: ApiCallKey;
  confidence?: number;
  rationale?: string;
  reason?: string;
  source?: 'auto' | 'user' | 'ai';
  action?: React.ReactNode;
  testid?: string;
}) {
  // 'ignored' and 'auto-ignored' are processed-but-set-aside — dim
  // them so the eye skips past. 'unstitched' is the active worklist
  // the user must triage — render at full strength.
  const dimmed = variant === 'ignored' || variant === 'auto-ignored';
  const strikeArrow = dimmed;
  const rightLabel = (() => {
    switch (variant) {
      case 'in-effect': return 'Bound api-call';
      case 'candidate': return 'Candidate api-call';
      case 'user': return 'Bound api-call';
      case 'ai': return 'AI proposes';
      case 'unstitched': return 'No api-call yet';
      case 'ignored': return 'No api-call';
      case 'auto-ignored': return 'No api-call';
    }
  })();
  const rightTint = (() => {
    switch (variant) {
      case 'in-effect': return 'text-green-700 dark:text-green-300';
      case 'candidate': return 'text-amber-700 dark:text-amber-300';
      case 'user': return 'text-blue-700 dark:text-blue-300';
      case 'ai': return 'text-purple-700 dark:text-purple-300';
      case 'unstitched': return 'text-red-700 dark:text-red-300';
      default: return 'text-slate-500 dark:text-slate-400';
    }
  })();
  const arrowTint = strikeArrow
    ? 'text-slate-400 dark:text-slate-600'
    : variant === 'in-effect' || variant === 'user'
    ? 'text-green-600 dark:text-green-400'
    : variant === 'ai'
    ? 'text-purple-600 dark:text-purple-400'
    : variant === 'candidate'
    ? 'text-amber-600 dark:text-amber-400'
    : variant === 'unstitched'
    ? 'text-red-600 dark:text-red-400'
    : 'text-slate-400 dark:text-slate-600';

  return (
    <li
      className={`border border-border rounded-lg overflow-hidden bg-white dark:bg-slate-800/40 ${dimmed ? 'opacity-70' : ''}`}
      data-testid={testid}
    >
      {/* ─── Handler ↔ api-call grid ─── */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-slate-700">
        {/* Left: handler */}
        <div className="p-3">
          <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">
            Handler
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {source && <ProvenanceBadge source={source} />}
            {variant === 'auto-ignored' && (
              <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded bg-slate-50 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                auto
              </span>
            )}
            <span className="font-medium text-slate-800 dark:text-slate-200 break-words">
              {handler.component ?? '(no component)'} · {handler.handlerFunction}
            </span>
          </div>
          <div className="text-xs text-muted-foreground font-mono mt-1 break-all">
            {handlerFile}{handlerLine != null && `:${handlerLine}`}
          </div>
          {(eventType || triggerElement) && (
            <div className="text-xs mt-1 text-slate-600 dark:text-slate-400">
              Trigger: {eventType ?? '?'} on &lt;{triggerElement ?? '?'}&gt;
            </div>
          )}
          {calledFunctions && calledFunctions.length > 0 && (
            <div className="text-xs mt-1 text-slate-600 dark:text-slate-400">
              Called: <span className="font-mono">{calledFunctions.slice(0, 6).join(', ')}{calledFunctions.length > 6 ? ` +${calledFunctions.length - 6}` : ''}</span>
            </div>
          )}
        </div>

        {/* Middle: arrow */}
        <div className={`hidden md:flex items-center justify-center px-2 ${arrowTint}`}>
          {strikeArrow ? (
            // Strike-through arrow for ignored rows.
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              <line x1="3" y1="21" x2="21" y2="3" strokeWidth={2} strokeLinecap="round" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          )}
        </div>

        {/* Right: api-call (or placeholder for ignored) */}
        <div className="p-3">
          <div className={`text-[10px] uppercase font-semibold ${rightTint} mb-1`}>
            {rightLabel}
          </div>
          {apiCall ? (
            <>
              <div className="font-mono text-sm text-slate-800 dark:text-slate-200 break-all">
                <span className={`inline-block px-1.5 py-0.5 rounded text-xs mr-1 ${methodTint(apiCall.method)}`}>
                  {apiCall.method}
                </span>
                {apiCall.endpoint}
              </div>
              <div className="text-xs text-muted-foreground mt-1 font-mono break-all">
                {apiCall.sourceFile}
              </div>
              {apiCall.sourceContext && (
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 font-mono">
                  {apiCall.sourceContext}
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-slate-400 italic">
              {reason ?? 'No api-call attribution.'}
            </div>
          )}
        </div>
      </div>

      {/* ─── Provenance / action bar ─── */}
      {(confidence != null || rationale || action) && (
        <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs flex-wrap flex-1 min-w-0">
            {confidence != null && (
              <span className="text-slate-500 shrink-0">
                conf {(confidence * 100).toFixed(0)}%
              </span>
            )}
            {rationale && (
              <span className="italic text-slate-600 dark:text-slate-400 truncate">
                {rationale}
              </span>
            )}
          </div>
          {action && <div className="flex gap-1.5 shrink-0">{action}</div>}
        </div>
      )}
    </li>
  );
}

/** HTTP-method colour vocabulary — match the URL stitch page. */
function methodTint(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
    case 'POST': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    case 'PUT':
    case 'PATCH': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
    case 'DELETE': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    default: return 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300';
  }
}

function StitchList({
  stitches,
  variant,
  onAccept,
}: {
  stitches: ClickStitch[];
  variant: RowVariant;
  onAccept?: (h: HandlerKey, c: ApiCallKey) => void;
}) {
  if (stitches.length === 0) {
    return <p className="text-sm text-muted-foreground">none</p>;
  }
  return (
    <ul className="space-y-2">
      {stitches.map((s, i) => (
        <ClickStitchRow
          key={i}
          testid="click-stitch"
          variant={variant}
          handler={s.handler}
          handlerFile={s.handler.handlerFile}
          triggerElement={s.handler.triggerElement}
          eventType={s.handler.eventType}
          apiCall={s.apiCall}
          confidence={s.confidence}
          rationale={s.rationale}
          source={s.source}
          action={
            onAccept && s.source !== 'user' ? (
              <button
                onClick={() => onAccept(s.handler, s.apiCall)}
                data-testid="click-stitch-accept"
                className="px-2 py-1 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700"
              >
                Accept
              </button>
            ) : undefined
          }
        />
      ))}
    </ul>
  );
}

/**
 * #980: Unstitched-handlers section. Owns:
 *   - Search + group-by-file toggle
 *   - Virtualised rendering (handles 10k+ rows)
 *   - Per-handler + per-group selection
 *   - Bulk "Mark as UI-only" against /click/ignore-bulk
 *
 * Parent loads the full unstitched set in one shot via `?all=true`
 * so all grouping/search/selection can run client-side.
 */
function UnstitchedSection({
  unstitched,
  totalUnstitched,
  projectId,
  onIgnore,
  onMutated,
  onError,
}: {
  unstitched: UnstitchedHandler[];
  totalUnstitched: number;
  projectId: string;
  onIgnore: (handler: HandlerKey) => Promise<void>;
  onMutated: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState<'file' | 'flat'>('file');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  // #987 review minor 4: auto-expand all groups when the total
  // unstitched is small so users don't have to click through to
  // see them. For large projects collapse-by-default keeps the
  // page scannable.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    if (totalUnstitched <= 20) {
      return new Set(unstitched.map(u => u.handlerFile));
    }
    return new Set();
  });
  const [bulkBusy, setBulkBusy] = useState(false);

  const keyOf = (h: UnstitchedHandler) =>
    `${h.handlerFile}|${h.handlerFunction}|${h.triggerElement}|${h.eventType}|${h.component ?? ''}`;

  // #987 review minor 1: prune selection to keys that still exist
  // in the current unstitched set. Handlers removed by a single-row
  // ignore (or by full refresh after analysis) would otherwise leave
  // ghost entries inflating the "N selected" badge.
  useEffect(() => {
    const live = new Set(unstitched.map(keyOf));
    setSelected(prev => {
      let changed = false;
      const n = new Set<string>();
      for (const k of prev) {
        if (live.has(k)) {
          n.add(k);
        } else {
          changed = true;
        }
      }
      return changed ? n : prev;
    });
  }, [unstitched]);

  // Filtered list — client-side text match across handler name,
  // file path, component name, and called-function names.
  const filtered = useMemo(() => {
    if (!search.trim()) return unstitched;
    const q = search.trim().toLowerCase();
    return unstitched.filter(u =>
      u.handlerFunction.toLowerCase().includes(q) ||
      u.handlerFile.toLowerCase().includes(q) ||
      (u.component ?? '').toLowerCase().includes(q) ||
      u.calledFunctions.some(c => c.toLowerCase().includes(q)),
    );
  }, [unstitched, search]);

  // Grouped view — by handler file. Sorted descending by group size so
  // the noisy buckets surface first.
  type Group = { file: string; handlers: UnstitchedHandler[] };
  const groups: Group[] = useMemo(() => {
    if (groupBy !== 'file') return [];
    const byFile = new Map<string, UnstitchedHandler[]>();
    for (const u of filtered) {
      const arr = byFile.get(u.handlerFile) ?? [];
      arr.push(u);
      byFile.set(u.handlerFile, arr);
    }
    return Array.from(byFile.entries())
      .map(([file, handlers]) => ({ file, handlers }))
      .sort((a, b) => b.handlers.length - a.handlers.length);
  }, [filtered, groupBy]);

  const toggleSelect = (key: string) => {
    setSelected(s => {
      const n = new Set(s);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  const toggleGroupSelect = (handlers: UnstitchedHandler[]) => {
    const keys = handlers.map(keyOf);
    setSelected(s => {
      const n = new Set(s);
      const allIn = keys.every(k => n.has(k));
      if (allIn) keys.forEach(k => n.delete(k));
      else keys.forEach(k => n.add(k));
      return n;
    });
  };

  const toggleExpand = (file: string) => {
    setExpanded(e => {
      const n = new Set(e);
      n.has(file) ? n.delete(file) : n.add(file);
      return n;
    });
  };

  const selectAllVisible = () => {
    setSelected(new Set(filtered.map(keyOf)));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkIgnore = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const byKey = new Map(filtered.map(u => [keyOf(u), u.handler]));
      const handlers = Array.from(selected).map(k => byKey.get(k)).filter(Boolean);
      const r = await apiFetch(`/projects/${projectId}/stitches/click/ignore-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handlers }),
      });
      if (!r.ok) {
        onError(`Bulk ignore failed: HTTP ${r.status}`);
        return;
      }
      setSelected(new Set());
      await onMutated();
    } finally {
      setBulkBusy(false);
    }
  };

  // Map handler → its file group expansion state so flat-mode also
  // works.
  const sectionTitle = useMemo(() => {
    if (search.trim() && filtered.length !== totalUnstitched) {
      return `Unstitched click handlers — ${filtered.length} match of ${totalUnstitched} across ${groups.length} file${groups.length === 1 ? '' : 's'}`;
    }
    return `Unstitched click handlers — ${totalUnstitched} across ${groups.length || new Set(unstitched.map(u => u.handlerFile)).size} files`;
  }, [filtered.length, groups.length, totalUnstitched, search, unstitched]);

  return (
    <Section
      title={sectionTitle}
      subtitle="No api-call attribution found. Bind manually, propose with AI, or mark as UI-only."
    >
      {unstitched.length === 0 ? (
        <p className="text-sm text-muted-foreground">All click handlers are stitched.</p>
      ) : (
        <>
          {/* Controls: search + group toggle + bulk action bar. */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <input
              type="text"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                // #987 review minor 2: selection is visible-scoped —
                // clear it when the filter changes so the user doesn't
                // accidentally bulk-act on rows they can't see.
                if (selected.size > 0) setSelected(new Set());
              }}
              placeholder="Filter by handler, file, component, called function…"
              data-testid="unstitched-search"
              className="px-2 py-1 text-sm rounded border border-border bg-background flex-1 min-w-[240px]"
            />
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>Group:</span>
              <button
                onClick={() => setGroupBy('file')}
                className={`px-2 py-1 rounded border transition-colors ${groupBy === 'file' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'border-border hover:bg-muted'}`}
              >
                File
              </button>
              <button
                onClick={() => setGroupBy('flat')}
                className={`px-2 py-1 rounded border transition-colors ${groupBy === 'flat' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'border-border hover:bg-muted'}`}
              >
                Flat
              </button>
            </div>
            {selected.size > 0 ? (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">{selected.size} selected</span>
                <button
                  onClick={bulkIgnore}
                  disabled={bulkBusy}
                  data-testid="bulk-mark-ui-only"
                  className="px-2 py-1 rounded bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {bulkBusy ? 'Working…' : `Mark ${selected.size} as UI-only`}
                </button>
                <button
                  onClick={clearSelection}
                  className="px-2 py-1 rounded border border-border hover:bg-muted"
                >
                  Clear
                </button>
              </div>
            ) : (
              <button
                onClick={selectAllVisible}
                disabled={filtered.length === 0}
                className="px-2 py-1 text-xs rounded border border-border text-muted-foreground hover:bg-muted disabled:opacity-50"
              >
                Select all {filtered.length}
              </button>
            )}
          </div>

          {groupBy === 'file' ? (
            <ul className="space-y-2">
              {groups.map(g => (
                <FileGroupRow
                  key={g.file}
                  group={g}
                  expanded={expanded.has(g.file)}
                  selectedKeys={selected}
                  onToggleExpand={() => toggleExpand(g.file)}
                  onToggleSelect={toggleSelect}
                  onToggleGroupSelect={() => toggleGroupSelect(g.handlers)}
                  onIgnore={onIgnore}
                  keyOf={keyOf}
                />
              ))}
            </ul>
          ) : (
            <VirtualFlatList
              handlers={filtered}
              selectedKeys={selected}
              onToggleSelect={toggleSelect}
              onIgnore={onIgnore}
              keyOf={keyOf}
            />
          )}
        </>
      )}
    </Section>
  );
}

/** Collapsed-by-default group row showing a file + handler count. */
function FileGroupRow({
  group,
  expanded,
  selectedKeys,
  onToggleExpand,
  onToggleSelect,
  onToggleGroupSelect,
  onIgnore,
  keyOf,
}: {
  group: { file: string; handlers: UnstitchedHandler[] };
  expanded: boolean;
  selectedKeys: Set<string>;
  onToggleExpand: () => void;
  onToggleSelect: (key: string) => void;
  onToggleGroupSelect: () => void;
  onIgnore: (handler: HandlerKey) => Promise<void>;
  keyOf: (h: UnstitchedHandler) => string;
}) {
  const keys = group.handlers.map(keyOf);
  const selectedInGroup = keys.filter(k => selectedKeys.has(k)).length;
  const allSelected = selectedInGroup === keys.length && keys.length > 0;
  const someSelected = selectedInGroup > 0 && !allSelected;
  return (
    <li className="border border-border rounded-lg overflow-hidden bg-white dark:bg-slate-800/40" data-testid="unstitched-file-group">
      <div className="flex items-center gap-3 px-3 py-2 bg-muted/30 cursor-pointer hover:bg-muted/50" onClick={onToggleExpand}>
        <input
          type="checkbox"
          checked={allSelected}
          ref={el => { if (el) el.indeterminate = someSelected; }}
          onChange={onToggleGroupSelect}
          onClick={e => e.stopPropagation()}
          aria-label={`Select all ${keys.length} handlers in ${group.file}`}
          className="cursor-pointer"
        />
        <span className="text-xs font-mono truncate flex-1" title={group.file}>{group.file}</span>
        <span className="text-xs text-muted-foreground shrink-0">{group.handlers.length} handler{group.handlers.length === 1 ? '' : 's'}</span>
        <span className="text-xs text-muted-foreground shrink-0">{expanded ? '▾' : '▸'}</span>
      </div>
      {expanded && (
        group.handlers.length > 50 ? (
          <VirtualFlatList
            handlers={group.handlers}
            selectedKeys={selectedKeys}
            onToggleSelect={onToggleSelect}
            onIgnore={onIgnore}
            keyOf={keyOf}
            embedded
          />
        ) : (
          <ul className="divide-y divide-border">
            {group.handlers.map(u => (
              <li key={keyOf(u)} className="flex items-start gap-2 px-3 py-2" data-testid="unstitched-handler">
                <input
                  type="checkbox"
                  checked={selectedKeys.has(keyOf(u))}
                  onChange={() => onToggleSelect(keyOf(u))}
                  aria-label={`Select ${u.handlerFunction}`}
                  className="mt-1 cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {u.component ?? '(no component)'} · {u.handlerFunction}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {u.eventType} on &lt;{u.triggerElement}&gt;{u.handlerLine ? ` · :${u.handlerLine}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => onIgnore(u.handler)}
                  data-testid="mark-ui-only"
                  className="px-2 py-1 text-xs rounded border border-border text-muted-foreground hover:bg-muted whitespace-nowrap"
                >
                  Mark UI-only
                </button>
              </li>
            ))}
          </ul>
        )
      )}
    </li>
  );
}

/** Virtualised flat list — scrollable window rendering only visible
 *  rows. Used for the flat view and for groups with > 50 handlers. */
function VirtualFlatList({
  handlers,
  selectedKeys,
  onToggleSelect,
  onIgnore,
  keyOf,
  embedded,
}: {
  handlers: UnstitchedHandler[];
  selectedKeys: Set<string>;
  onToggleSelect: (key: string) => void;
  onIgnore: (handler: HandlerKey) => Promise<void>;
  keyOf: (h: UnstitchedHandler) => string;
  embedded?: boolean;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: handlers.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 52,
    overscan: 8,
  });
  // Cap the viewport height: embedded inside a group, smaller; standalone,
  // a comfortable scroll window.
  const maxHeight = embedded ? 480 : 720;
  const height = Math.min(maxHeight, handlers.length * 52);
  return (
    <div
      ref={parentRef}
      data-testid="unstitched-virtual"
      className="overflow-auto border-t border-border"
      style={{ height }}
    >
      <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
        {rowVirtualizer.getVirtualItems().map(virtualRow => {
          const u = handlers[virtualRow.index];
          const k = keyOf(u);
          return (
            <div
              key={k}
              data-testid="unstitched-handler"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className="flex items-start gap-2 px-3 py-2 border-b border-border last:border-b-0"
            >
              <input
                type="checkbox"
                checked={selectedKeys.has(k)}
                onChange={() => onToggleSelect(k)}
                aria-label={`Select ${u.handlerFunction}`}
                className="mt-1 cursor-pointer"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {u.component ?? '(no component)'} · {u.handlerFunction}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {u.handlerFile}{u.handlerLine ? `:${u.handlerLine}` : ''} · {u.eventType} on &lt;{u.triggerElement}&gt;
                </div>
              </div>
              <button
                onClick={() => onIgnore(u.handler)}
                data-testid="mark-ui-only"
                className="px-2 py-1 text-xs rounded border border-border text-muted-foreground hover:bg-muted whitespace-nowrap shrink-0"
              >
                Mark UI-only
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
